import {
    Checkbox,
    DatePicker,
    DateTimePicker,
    Flex,
    NumberField,
    Select,
    Table,
    TextField,
    TimePicker,
} from '@approvalmax/ui/src/components';
import { FC } from 'react';
import { useFormContext } from 'react-hook-form';

import { customRecordFieldNames, fieldMinHeight } from './NetSuiteLineCustomField.constants';
import { useControlProps, useItems } from './NetSuiteLineCustomField.hooks';
import { NetSuiteLineCustomFieldProps } from './NetSuiteLineCustomField.types';

export const NetSuiteLineCustomField: FC<NetSuiteLineCustomFieldProps> = (props) => {
    const { cellProps, field, sublistType, fieldName, disabled } = props;

    const { setValue } = useFormContext();

    const { getItems, isLoadingItems, setQuery } = useItems({
        cellProps,
        field,
        sublistType,
    });

    const { generalProps, numberFieldProps, inputProps, selectProps } = useControlProps({
        disabled,
        field,
        setQuery,
        isLoadingItems,
    });

    switch (field.netSuiteField?.type) {
        case 'CheckBox': {
            return (
                <Table.FormCell name={`${fieldName}.valueBoolean`} {...cellProps}>
                    {(params) => (
                        <Flex alignItems='center' height={fieldMinHeight}>
                            <Checkbox {...params} {...generalProps} />
                        </Flex>
                    )}
                </Table.FormCell>
            );
        }

        case 'Currency': {
            return (
                <Table.FormCell name={`${fieldName}.valueDecimal`} {...cellProps}>
                    {(params) => <NumberField {...params} {...numberFieldProps} allowFloat precision={2} />}
                </Table.FormCell>
            );
        }

        case 'DecimalNumber': {
            return (
                <Table.FormCell name={`${fieldName}.valueDecimal`} {...cellProps}>
                    {(params) => <NumberField {...params} {...numberFieldProps} allowFloat precision={8} />}
                </Table.FormCell>
            );
        }

        case 'IntegerNumber': {
            return (
                <Table.FormCell name={`${fieldName}.valueLong`} {...cellProps}>
                    {(params) => <NumberField {...params} {...numberFieldProps} />}
                </Table.FormCell>
            );
        }

        case 'Percent': {
            return (
                <Table.FormCell name={`${fieldName}.valueDecimal`} {...cellProps}>
                    {(params) => (
                        <NumberField {...params} {...numberFieldProps} allowFloat max={100} min={0} precision={4} />
                    )}
                </Table.FormCell>
            );
        }

        case 'Date': {
            return (
                <Table.FormCell name={`${fieldName}.valueDate`} {...cellProps}>
                    {(params) => <DatePicker {...params} {...selectProps} />}
                </Table.FormCell>
            );
        }

        case 'TimeOfDay': {
            return (
                <Table.FormCell name={`${fieldName}.valueDate`} {...cellProps}>
                    {(params) => <TimePicker {...params} {...selectProps} />}
                </Table.FormCell>
            );
        }

        case 'Datetime': {
            return (
                <Table.FormCell name={`${fieldName}.valueDate`} {...cellProps}>
                    {(params) => <DateTimePicker {...params} {...selectProps} />}
                </Table.FormCell>
            );
        }

        case 'Hyperlink':
        case 'PhoneNumber':
        case 'EMailAddress': {
            return (
                <Table.FormCell name={`${fieldName}.valueString`} {...cellProps}>
                    {(params) => <TextField {...params} {...inputProps} />}
                </Table.FormCell>
            );
        }

        case 'TextArea':
        case 'LongText':
        case 'FreeFormText': {
            return (
                <Table.FormCell name={`${fieldName}.valueString`} {...cellProps}>
                    {(params) => <TextField {...params} {...inputProps} multiline rows={1} autoHeight />}
                </Table.FormCell>
            );
        }

        case 'ListRecord': {
            return (
                <Table.FormCell name={`${fieldName}.${customRecordFieldNames.valueRefId}`} {...cellProps}>
                    {({ name, ref }) => {
                        const items = getItems(customRecordFieldNames.valueRef);

                        return (
                            <Select
                                {...selectProps}
                                name={name}
                                activatorRef={ref}
                                items={items}
                                onChange={(value) => {
                                    setValue(
                                        name.replace(/Id$/, ''),
                                        items.find((item) => item.id === value)
                                    );
                                }}
                            />
                        );
                    }}
                </Table.FormCell>
            );
        }

        case 'MultipleSelect': {
            return (
                <Table.FormCell name={`${fieldName}.${customRecordFieldNames.valueRefsIds}`} {...cellProps}>
                    {({ name, ref }) => {
                        const items = getItems(customRecordFieldNames.valueRefs);

                        return (
                            <Select
                                {...selectProps}
                                name={name}
                                activatorRef={ref}
                                items={items}
                                multiple
                                onChange={(value) => {
                                    setValue(
                                        name.replace(/Ids$/, ''),
                                        items.filter((item) => value.includes(item.id))
                                    );
                                }}
                            />
                        );
                    }}
                </Table.FormCell>
            );
        }

        default: {
            return <Table.Cell {...cellProps} disabled />;
        }
    }
};

NetSuiteLineCustomField.displayName = 'NetSuiteLineCustomField';
